/* La mappa dei tasti, quella che si apre con `?`.
 *
 * Legge la stessa tabella del gestore (`keys.ts`): due elenchi separati
 * divergono al primo tasto spostato, e chi impara la mappa impara quello
 * sbagliato.
 *
 * **E' modale, e non solo a vedersi.** Prima era una finestra a schermo intero
 * che *sembrava* modale: nessun `aria-modal`, nessun fuoco che entrasse, nessun
 * confine da cui non uscire tabulando, e i tasti dei verdetti vivi sotto. Chi
 * ascolta continuava a tabulare nel banco dietro senza sapere di esserne fuori,
 * e una «A» battuta mentre si legge la mappa confermava un candidato che nessuno
 * stava guardando. Sono tre difetti diversi con la stessa causa: una finestra
 * dichiarata a parole e non nella struttura.
 *
 * Si chiude con `Esc` e col comando visibile — il codice di disegno vieta le
 * trappole da cui non si esce con un tasto, e un'uscita che vive solo sulla
 * tastiera non e' un'uscita per tutti — e restituisce il fuoco a chi lo aveva:
 * senza, chi ha aperto la mappa da un comando si ritrova all'inizio della pagina
 * e deve ritabulare fino al punto in cui stava.
 */
import { useEffect, useId, useRef } from 'react'

import { BINDINGS, HANDS } from './keys'
import { useT } from './i18n/LocaleProvider'

/* Cio' che riceve il fuoco dentro la mappa. Non c'e' niente da scrivere qui:
 * sono comandi e collegamenti, e l'elenco serve solo a sapere dove sta il primo
 * e dove l'ultimo. */
const FOCUSABLE = 'button, [href], [tabindex]:not([tabindex="-1"])'

export function KeyHelp({ onClose }: { onClose: () => void }): JSX.Element {
  const t = useT()
  const box = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    const before = document.activeElement
    box.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    return () => {
      // Solo se e' ancora nella pagina: un comando smontato insieme alla mappa
      // non e' un posto dove rimettere il fuoco.
      if (before instanceof HTMLElement && before.isConnected) before.focus()
    }
  }, [])

  /** Il giro del fuoco dentro la mappa: dall'ultimo si torna al primo. */
  function contain(event: React.KeyboardEvent): void {
    const inside = Array.from(box.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    if (inside.length === 0) return
    const first = inside[0]
    const last = inside[inside.length - 1]
    if (!first || !last) return
    const now = document.activeElement
    if (event.shiftKey ? now === first || !box.current?.contains(now) : now === last) {
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
    }
  }

  return (
    <div
      className="mappa-tasti"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={box}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // Fermato qui: sotto c'e' il gestore del banco, e per lui `Esc` vuol
          // dire «esci dal modo» — uscirebbe anche da quello.
          event.stopPropagation()
          event.preventDefault()
          onClose()
          return
        }
        if (event.key === 'Tab') contain(event)
      }}
    >
      <div className="mappa-dentro">
        {/* Il titolo e il comando di chiusura stanno in testa: il fuoco entrava
            sul solo comando, che stava in fondo, e il contenitore scorreva
            fino a nascondere la prima sezione (GI-281). */}
        <div className="mappa-testa">
          <h2 id={titleId}>{t('keys.help')}</h2>
          <button type="button" className="cockpit-azione cockpit-azione-piccola" onClick={onClose}>
            {t('keys.close')}
          </button>
        </div>
        {HANDS.map(([hand, title]) => (
          <section key={hand}>
            <h3>{t(title)}</h3>
            <ul className="motivi">
              {BINDINGS.filter((one) => one.hand === hand).map((one) => (
                <li key={one.keys.join('')}>
                  {one.keys.map((key) => (
                    <kbd key={key}>{key}</kbd>
                  ))}{' '}
                  {t(one.what)}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
